import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, of } from 'rxjs';
import { OrgCountryEnum } from '@rezevent/api-types';
import { UserRoleEnum } from './user-roles.enum';
import { AuthBaseService } from './auth-base.service';
import { TRANSLATIONS_ENABLED, TranslationService } from './translation.service';

export const AUTH_TOKEN_KEY = 'auth_token';
export const AUTH_TOKEN_EXP_KEY = 'auth_expires';
export const AUTH_COUNTRY_KEY = 'auth_country';

export interface JwtPayload {
  _id: string;
  role: UserRoleEnum;
  organization: string | null;
  iat: number;
  exp: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService extends AuthBaseService {
  protected readonly storageKey = AUTH_TOKEN_KEY;
  protected readonly storageExpKey = AUTH_TOKEN_EXP_KEY;

  readonly typedPayload$ = this.tokenPayload$.pipe(
    map(p => p as JwtPayload | null)
  );

  private _country$ = new BehaviorSubject<OrgCountryEnum | null>(
    localStorage.getItem(AUTH_COUNTRY_KEY) as OrgCountryEnum | null
  );
  readonly country$ = this._country$.asObservable();
  get country(): OrgCountryEnum | null {
    return this._country$.getValue();
  }

  private translations = inject(TranslationService);
  private translationsEnabled = inject(TRANSLATIONS_ENABLED);

  constructor() {
    super();
    this.init();
  }

  authenticate(token: string, country: OrgCountryEnum) {
    const payload = this.decodeToken(token) as JwtPayload | null;
    if (payload) {
      this.storeToken(token, payload.exp);
      localStorage.setItem(AUTH_COUNTRY_KEY, country);
      this._country$.next(country);
      if (!this.translationsEnabled) {
        this.router.navigate(['/']);
        return;
      }
      // Navigate even if the translation file fails to load
      this.translations
        .load(country)
        .pipe(catchError(() => of(void 0)))
        .subscribe(() => this.router.navigate(['/']));
    } else {
      console.error('Invalid token during authentication');
    }
  }

  logout() {
    this.clearStorage();
    localStorage.removeItem(AUTH_COUNTRY_KEY);
    this._country$.next(null);
    this.router.navigate(['/login']);
  }
}
